import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CardsPosition from "../CheckOut/CardsPosition";
import { useCart } from "./CartContext";
import { FaLongArrowAltRight } from "react-icons/fa";
import "./CartPage.css";

const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    Number(n || 0),
  );

const CartPage = ({ shippingFlat = 8, vatRate = 0.11, onCheckout }) => {
  const {
    cartItems,
    // addToCart,
    removeItem,
    increaseQty,
    decreaseQty,
    subtotal,
    termsAccepted,
    setTermsAccepted,
  } = useCart();

  const cartCount = cartItems.reduce((total, item) => total + item.qty, 0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const computedSubtotal = useMemo(
    () => cartItems.reduce((s, it) => s + Number(it.price) * Number(it.qty), 0),
    [cartItems],
  );
  const sub = typeof subtotal === "number" ? subtotal : computedSubtotal;

  const [coupon, setCoupon] = useState("");
  const [couponMsg, setCouponMsg] = useState("");

  const code = coupon.trim().toLowerCase();

  // demo coupon logic (optional)
  const discount = useMemo(() => {
    if (code === "righttrack10") return sub * 0.1;
    return 0;
  }, [code, sub]);

  const shipping = shippingFlat;
  const vat = Math.max(0, (sub - discount) * vatRate);
  // const total = Math.max(0, sub - discount + (sub > 0 ? shipping : 0) + vat);
  const total = Math.max(0, sub - discount + vat);

  const applyCoupon = (e) => {
    e.preventDefault();
    if (!coupon.trim()) return setCouponMsg("Please enter a coupon code.");
    if (code === "righttrack10")
      return setCouponMsg("✅ Coupon applied: 10% off");
    setCouponMsg("❌ Invalid coupon code.");
  };

  const navigate = useNavigate();
  const handleCheckout = () => {
    if (onCheckout) return onCheckout({ cartItems, total });
    navigate("/CheckOut");
    alert("Proceed to checkout");
  };

  return (
    <div className="checkout-container">
      <CardsPosition currentStep={2} />
      {/* <div>
        {cartItems.map((item) => (
          <div key={item.id}>
            {item.title}
            <button onClick={() => removeItem(item.id)}>Remove</button>
          </div>
        ))}
      </div> */}
      <div className="cartM">
        <div className="cartM__top">
          <div className="cartM__titles">
            <h1 className="cartM__h1">Cart</h1>
            <p className="cartM__p">
              Review your items and proceed when you’re ready.
            </p>
          </div>
          <div className="cartM__meta">
            <span className="cartM__pill">
              {cartCount} {cartCount === 1 ? "item" : "items"}
            </span>
          </div>
        </div>

        <div className="cartM__grid">
          {/* LEFT: items */}
          <section className="cartM__card">
            <div className="cartM__tableHead">
              <div className="th product">Product</div>
              <div className="th price">Price</div>
              <div className="th qty">Quantity</div>
              <div className="th sub">Subtotal</div>
            </div>

            <div className="cartM__rows">
              {cartItems.length === 0 ? (
                <div className="cartM__empty">
                  <div className="cartM__emptyIcon">🛒</div>
                  <div>
                    <h3>Your cart is empty</h3>
                    <p>Add products to see them here.</p>
                  </div>
                </div>
              ) : (
                cartItems.map((it) => {
                  // ✅ If your item keys differ, change here:
                  const id = it.id; // or it._id
                  const title = it.title || it.name;
                  const price = Number(it.price || 0);
                  const qty = Number(it.qty || it.quantity || 1);
                  const img = it.image || it.img || it.cover;

                  return (
                    <div className="cartM__row" key={id}>
                      <div className="cell product">
                        <button
                          className="cartM__remove"
                          onClick={() => removeItem(id)}
                          aria-label="Remove item"
                          title="Remove"
                        >
                          ✕
                        </button>

                        <div className="cartM__prod">
                          <img className="cartM__img" src={img} alt={title} />
                          <div className="cartM__info">
                            <div className="cartM__name">{title}</div>
                            <div className="cartM__hint">
                              Vinyl • Premium Press
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="cell price">{money(price)}</div>

                      <div className="cell qty">
                        <div className="cartM__qty">
                          <button
                            className="cartM__qbtn"
                            onClick={() => decreaseQty(id, qty - 1)}
                            aria-label="Decrease"
                          >
                            −
                          </button>

                          <input
                            className="cartM__qinput"
                            value={qty}
                            // onChange={(e) => decreaseQty(id, Number(e.target.value || 1))}
                            readonly
                            inputMode="numeric"
                            aria-label="Quantity"
                          />

                          <button
                            className="cartM__qbtn"
                            onClick={() => increaseQty(id, qty + 1)}
                            aria-label="Increase"
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div className="cell sub strong">
                        {money(price * qty)}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* footer: coupon */}
            <div className="cartM__actions">
              <form className="cartM__coupon" onSubmit={applyCoupon}>
                <input
                  className="cartM__couponInput"
                  placeholder="Coupon code (try: RIGHTTRACK10)"
                  value={coupon}
                  onChange={(e) => {
                    setCoupon(e.target.value);
                    setCouponMsg("");
                  }}
                />
                <button className="cartM__btn cartM__btnGhost" type="submit">
                  Apply coupon
                </button>
              </form>

              <div className="cartM__couponMsg" aria-live="polite">
                {couponMsg}
              </div>
            </div>
          </section>

          {/* RIGHT: totals */}
          <aside className="cartM__summary">
            <div className="cartM__summaryTop">
              <h2 className="cartM__summaryTitle">Cart totals</h2>
              <div className="cartM__summaryHint">Secure checkout</div>
            </div>

            <div className="cartM__sumRows">
              <SumRow label="Subtotal" value={money(sub)} />
              <SumRow
                label="Discount"
                value={discount ? `- ${money(discount)}` : money(0)}
                muted={!discount}
              />
              {/* {sub > 0 && <SumRow label="Shipping" value={money(shipping)} />} */}
              <SumRow
                label={`VAT (${Math.round(vatRate * 100)}%)`}
                value={money(vat)}
              />
            </div>

            <div className="cartM__divider" />

            <div className="cartM__total">
              <span>Total</span>
              <span className="cartM__totalValue">{money(total)}</span>
            </div>

            {/* <button
            className="cartM__btn cartM__btnPrimary"
            disabled={cartItems.length === 0}
            onClick={handleCheckout}
          >
            Proceed to checkout
          </button> */}
            {/* <label className={`terms ${errors.terms ? "terms-error" : ""}`}> */}

            <button
              onClick={handleCheckout}
              className="card-view-btn mb-2"
              disabled={cartItems.length === 0}
            >
              <div className="card-view-final">
                <span className="card-view"> Proceed to checkout</span>
                <span className="arrow">
                  <FaLongArrowAltRight size={20} />
                </span>
              </div>
            </button>
            {/* <label className="terms">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => {
                  setTermsAccepted(e.target.checked);
                }}
                // ref={termsRef}
              />
              I have read and agree to the website terms and conditions{" "}
              <span className="star">*</span>
            </label> */}
            <div className="cartM__trust">
              <div className="cartM__trustItem">🔒 SSL Secure</div>
              <div className="cartM__trustItem">💳 Cards & Wallets</div>
              <div className="cartM__trustItem">📦 Fast Shipment</div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};
function SumRow({ label, value, muted }) {
  return (
    <div className={`cartM__sumRow ${muted ? "muted" : ""}`}>
      <span>{label}</span>
      <span className="cartM__sumVal">{value}</span>
    </div>
  );
}
export default CartPage;
